import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import { Box } from '@/shared/ui';

import type { ExtendedSeriesOptionsType } from '@/features/_shared/chart/types/highcharts';
import CurveComparisonSelector from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector';
import { populateTemplates } from '@/features/markets/components/Chart/ForwardCurveChartBlock/populateTemplate';
import type { TemplateChartSeries } from '@/shared/types/charts';

const rootSx = { display: 'flex', mt: 2 };

const ButtonSection = ({
  chartSeries,
  templateChartSeries,
  comparisonTemplateSeries,
  setComparisonTemplateSeries
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  templateChartSeries: TemplateChartSeries[] | undefined;
  comparisonTemplateSeries: TemplateChartSeries[];
  setComparisonTemplateSeries: Dispatch<SetStateAction<TemplateChartSeries[]>>;
}) => {
  const transactionDates = useMemo(
    () =>
      comparisonTemplateSeries
        .map(template => template.symbol.transactionDate)
        .filter((value): value is string => value !== undefined)
        .reduce<string[]>((acc, curr) => (acc.includes(curr) ? acc : [...acc, curr]), []),
    [comparisonTemplateSeries]
  );

  const parseTransactionDatesToTemplates = useCallback(
    (transactionDatesStrings: string[]) => {
      if (templateChartSeries === undefined) return;
      const templates = transactionDatesStrings.flatMap(transactionDate =>
        populateTemplates(transactionDate, templateChartSeries)
      );
      setComparisonTemplateSeries(templates);
    },
    [setComparisonTemplateSeries, templateChartSeries]
  );

  const populateTemplateSeriesWith = useCallback(
    (selectedDateString: string, comparisonSelectorIndex: number) => {
      const newTransactionDates = [...transactionDates];
      newTransactionDates[comparisonSelectorIndex] = selectedDateString;
      parseTransactionDatesToTemplates(newTransactionDates);
    },
    [parseTransactionDatesToTemplates, transactionDates]
  );

  const deleteFromTemplateSeriesWith = useCallback(
    (comparisonSelectorIndex: number) => {
      const newTransactionDates = transactionDates.filter(
        (_, index) => index !== comparisonSelectorIndex
      );
      parseTransactionDatesToTemplates(newTransactionDates);
    },
    [parseTransactionDatesToTemplates, transactionDates]
  );

  return (
    <Box sx={rootSx}>
      <CurveComparisonSelector
        key={`comparisonSelector${1}`}
        chartSeries={chartSeries}
        comparisonSelectorIndex={0}
        deleteFromTemplateSeriesWith={deleteFromTemplateSeriesWith}
        label='Add date comparison'
        populateTemplateSeriesWith={populateTemplateSeriesWith}
        transactionDates={transactionDates}
      />
      {transactionDates.length > 0 && (
        <CurveComparisonSelector
          key={`comparisonSelector${2}`}
          chartSeries={chartSeries}
          comparisonSelectorIndex={1}
          deleteFromTemplateSeriesWith={deleteFromTemplateSeriesWith}
          label='Add date comparison'
          populateTemplateSeriesWith={populateTemplateSeriesWith}
          transactionDates={transactionDates}
        />
      )}
    </Box>
  );
};
export default ButtonSection;
